import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar, LayoutGrid, Eye, EyeOff } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
  Switch,
} from '@jira-clone/ui'
import { useQuotationDisplaySettings, useQuotationDisplaySettingMutations } from '../../shared/hooks/useQuotationDisplaySettings'
import { getApiErrorMessage } from '../../shared/utils/apiError'

interface DraftState {
  id?: string
  name: string
  showHsnColumn: boolean
  showTaxSummary: boolean
  showSkuColumn: boolean
  showSerialNumbers: boolean
  showThumbnails: boolean
  showDescFullWidth: boolean
  hideSubtotalForGroups: boolean
  hidePlaceOfSupply: boolean
  displayUnit: 'short' | 'full'
  isDefault: boolean
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  showHsnColumn: true,
  showTaxSummary: true,
  showSkuColumn: true,
  showSerialNumbers: true,
  showThumbnails: false,
  showDescFullWidth: false,
  hideSubtotalForGroups: false,
  hidePlaceOfSupply: false,
  displayUnit: 'full',
  isDefault: false,
  isActive: true,
}

const SETTINGS = [
  { key: 'showHsnColumn', label: 'Show HSN/SAC Column', description: 'Display HSN/SAC codes in line items' },
  { key: 'showTaxSummary', label: 'Show Tax Summary', description: 'Display tax breakdown (CGST/SGST/IGST)' },
  { key: 'showSkuColumn', label: 'Show SKU Column', description: 'Display SKU codes in line items' },
  { key: 'showSerialNumbers', label: 'Show Serial Numbers', description: 'Display row numbers in line items' },
  { key: 'showThumbnails', label: 'Show Thumbnails', description: 'Display product/service thumbnails' },
  { key: 'showDescFullWidth', label: 'Description Full Width', description: 'Expand description to full width' },
  { key: 'hideSubtotalForGroups', label: 'Hide Subtotal for Groups', description: 'Hide subtotal rows for grouped items' },
  { key: 'hidePlaceOfSupply', label: 'Hide Place of Supply', description: 'Hide place of supply field' },
  { key: 'displayUnit', label: 'Display Unit', description: 'Show unit of measurement' },
] as const

export default function QuotationDisplaySettingAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: settings = [], isLoading } = useQuotationDisplaySettings({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove, setDefault } = useQuotationDisplaySettingMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (setting: any) => {
    setDraft({
      id: setting.id,
      name: setting.name,
      showHsnColumn: setting.showHsnColumn ?? true,
      showTaxSummary: setting.showTaxSummary ?? true,
      showSkuColumn: setting.showSkuColumn ?? true,
      showSerialNumbers: setting.showSerialNumbers ?? true,
      showThumbnails: setting.showThumbnails ?? false,
      showDescFullWidth: setting.showDescFullWidth ?? false,
      hideSubtotalForGroups: setting.hideSubtotalForGroups ?? false,
      hidePlaceOfSupply: setting.hidePlaceOfSupply ?? false,
      displayUnit: setting.displayUnit === 'short' ? 'short' : 'full',
      isDefault: setting.isDefault,
      isActive: setting.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Name is required.')
      return
    }
    try {
      const payload = {
        name: draft.name.trim(),
        showHsnColumn: draft.showHsnColumn,
        showTaxSummary: draft.showTaxSummary,
        showSkuColumn: draft.showSkuColumn,
        showSerialNumbers: draft.showSerialNumbers,
        showThumbnails: draft.showThumbnails,
        showDescFullWidth: draft.showDescFullWidth,
        hideSubtotalForGroups: draft.hideSubtotalForGroups,
        hidePlaceOfSupply: draft.hidePlaceOfSupply,
        displayUnit: draft.displayUnit,
        isDefault: draft.isDefault,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Display settings updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Display settings created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update these settings' : 'create these settings'))
    }
  }

  const handleDelete = async (setting: any) => {
    if (!confirm(`Delete "${setting.name}"? This can't be undone.`)) return
    setDeletingId(setting.id)
    try {
      await remove.mutateAsync(setting.id)
      toast.success('Display settings deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete these settings'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (setting: any) => {
    try {
      await setStatus.mutateAsync({ id: setting.id, isActive: !setting.isActive })
      toast.success(setting.isActive ? 'Settings deactivated.' : 'Settings activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle settings status'))
    }
  }

  const handleSetDefault = async (setting: any) => {
    try {
      await setDefault.mutateAsync(setting.id)
      toast.success('Default display settings updated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'set as default'))
    }
  }

  const renderSettingsList = (setting: DraftState | any) => (
    <div className="space-y-1.5">
      {SETTINGS.map((s) => (
        <div key={s.key} className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {setting[s.key as keyof DraftState] ? (
              <Eye className="h-4 w-4 text-green-600" />
            ) : (
              <EyeOff className="h-4 w-4 text-gray-400" />
            )}
            <span className="text-xs text-foreground">{s.label}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {setting[s.key as keyof DraftState] ? 'On' : 'Off'}
          </span>
        </div>
      ))}
    </div>
  )

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Document Display Settings</h2>
          <p className="text-sm text-muted-foreground">Manage display settings for quotations and invoices.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Settings Profile
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search settings profiles..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : settings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No display settings yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {settings.map((setting) => (
            <Card key={setting.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <LayoutGrid className="h-5 w-5 text-muted-foreground" />
                      <Badge variant={setting.isActive ? 'default' : 'secondary'} className="text-xs">
                        {setting.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {setting.isDefault && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{setting.name}</p>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {renderSettingsList(setting)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(setting)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {setting.isDefault ? (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(setting)}
                        title="Set as Default"
                      >
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(setting)}
                      title={setting.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {setting.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(setting)}
                      disabled={deletingId === setting.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Display Settings' : 'New Display Settings Profile'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label htmlFor="ds-name">Profile Name *</Label>
              <Input
                id="ds-name"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Standard Quotation View"
              />
            </div>

            <Separator className="my-2" />

            <h4 className="text-sm font-medium text-foreground">Display Options</h4>
            <p className="text-xs text-muted-foreground">Configure which columns and sections appear in generated documents.</p>

            <div className="grid gap-3 md:grid-cols-2">
              {SETTINGS.map((s) => (
                <div key={s.key} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-normal cursor-pointer">
                      <input
                        type="checkbox"
                        checked={draft[s.key as keyof DraftState] as boolean}
                        onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.checked }))}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-sm text-foreground">{s.label}</span>
                    </Label>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{s.description}</span>
                </div>
              ))}
            </div>

            <Separator className="my-2" />

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  id="ds-default"
                  type="checkbox"
                  checked={draft.isDefault}
                  onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
                />
                <label htmlFor="ds-default" className="cursor-pointer">Set as default display settings</label>
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Settings Profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}